/* theme-exempt-file: this is an always-dark log console (dark bg + light-grey text) by design,
   identical in both themes — the gray text is intentional on the black console surface. */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useGetRecentLogsQuery } from "@/store/admin_api";
import { useLogStreamSocket } from "@/hooks/useLogStreamSocket";
import { LogEntry } from "@/model/interface/LogEntry";

const MAX_LINES = 2000; // hard cap in the browser so memory stays flat
const LEVELS = ["ALL", "DEBUG", "INFO", "WARN", "ERROR"] as const;
const LEVEL_ORDER: Record<string, number> = { TRACE: 0, DEBUG: 1, INFO: 2, WARN: 3, ERROR: 4 };

// The console background is always dark, so use explicit light colors (not theme tokens, which are
// dark in light mode and would be invisible here).
const levelColor = (level: string): string => {
  switch (level) {
    case "ERROR": return "text-red-400";
    case "WARN": return "text-amber-400";
    case "DEBUG": return "text-gray-400";
    case "TRACE": return "text-gray-500";
    default: return "text-gray-100";
  }
};

const fmtTime = (ts: number) => new Date(ts).toLocaleTimeString(undefined, { hour12: false }) +
  "." + String(ts % 1000).padStart(3, "0");

export default function LiveLogs() {
  const { data: recent } = useGetRecentLogsQuery();

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [paused, setPaused] = useState(false);
  const [minLevel, setMinLevel] = useState<string>("ALL");
  const [filter, setFilter] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const seededRef = useRef(false);

  // Seed once from the backfill buffer.
  useEffect(() => {
    if (recent && !seededRef.current) {
      seededRef.current = true;
      setLogs(recent.slice(-MAX_LINES));
    }
  }, [recent]);

  const handleLog = useCallback((entry: LogEntry) => {
    if (paused) return;
    setLogs((prev) => {
      const next = prev.length >= MAX_LINES ? prev.slice(prev.length - MAX_LINES + 1) : prev;
      return [...next, entry];
    });
  }, [paused]);

  useLogStreamSocket(!paused, handleLog);

  const visible = useMemo(() => {
    const threshold = minLevel === "ALL" ? -1 : LEVEL_ORDER[minLevel];
    const needle = filter.trim().toLowerCase();
    return logs.filter((l) => {
      if (threshold >= 0 && (LEVEL_ORDER[l.level] ?? 2) < threshold) return false;
      if (needle && !(`${l.logger} ${l.message} ${l.thread}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [logs, minLevel, filter]);

  // Auto-scroll to bottom on new lines.
  useEffect(() => {
    if (autoScroll && !paused && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible, autoScroll, paused]);

  const copyVisible = () => {
    const text = visible.map((l) => `${fmtTime(l.timestamp)} ${l.level} [${l.thread}] ${l.logger} - ${l.message}`).join("\n");
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied visible logs"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold mb-1">Live Logs</h2>
        <p className="text-muted-foreground text-sm">
          Real-time application logs (in-memory only — nothing is stored). Showing the last {MAX_LINES} lines.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={minLevel} onValueChange={setMinLevel}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LEVELS.map((l) => <SelectItem key={l} value={l}>{l === "ALL" ? "All levels" : `${l}+`}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter (logger, thread, message)…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
        <Button variant={paused ? "default" : "outline"} size="sm" onClick={() => setPaused((p) => !p)}>
          {paused ? "Resume" : "Pause"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setLogs([])}>Clear</Button>
        <Button variant="outline" size="sm" onClick={copyVisible}>Copy</Button>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground ml-1">
          <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
          Auto-scroll
        </label>
        <Badge variant="secondary" className="ml-auto">{visible.length} shown · {logs.length} buffered</Badge>
      </div>

      <div
        ref={scrollRef}
        className="h-[65vh] overflow-auto rounded-md border bg-black/90 p-3 font-mono text-xs leading-relaxed"
      >
        {visible.length === 0 ? (
          <div className="text-gray-400">Waiting for logs…</div>
        ) : (
          visible.map((l, i) => (
            <div key={i} className="whitespace-pre-wrap break-words">
              <span className="text-gray-500">{fmtTime(l.timestamp)}</span>{" "}
              <span className={levelColor(l.level)}>{l.level.padEnd(5)}</span>{" "}
              <span className="text-sky-400">{l.logger}</span>{" "}
              <span className="text-gray-500">[{l.thread}]</span>{" "}
              <span className={levelColor(l.level)}>{l.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
